import { useEffect, useState } from "react";
import PageHeader from "../components/layout/PageHeader";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../components/ui/select";
import { Shield, Filter } from "lucide-react";

const ACTIONS = ["login", "logout", "create", "update", "delete", "cancel", "pay", "open", "close"];
const ENTITIES = ["auth", "user", "branch", "operator", "game", "play_type", "number_limit", "ticket", "draw_result", "cash_session"];

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const ACTION_COLOR = {
  login: "bg-blue-100 text-blue-700",
  logout: "bg-slate-100 text-slate-600",
  create: "bg-green-100 text-green-700",
  update: "bg-yellow-100 text-yellow-800",
  delete: "bg-red-100 text-red-700",
  cancel: "bg-red-100 text-red-700",
  pay: "bg-emerald-100 text-emerald-800",
  open: "bg-green-100 text-green-700",
  close: "bg-slate-100 text-slate-700",
};

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [action, setAction] = useState("all");
  const [entity, setEntity] = useState("all");
  const [from, setFrom] = useState(daysAgo(1));
  const [to, setTo] = useState(todayStr());
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const params = { limit: 300, date_from: from, date_to: to };
    if (action !== "all") params.action = action;
    if (entity !== "all") params.entity_type = entity;
    const { data } = await api.get("/audit-logs", { params });
    setLogs(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="flex-1 bg-slate-50 min-h-full">
      <PageHeader
        title="Bitácora de Auditoría"
        subtitle="Historial inmutable de acciones críticas del sistema"
        testId="audit-header"
      />

      <div className="p-6 sm:p-8 space-y-4">
        <div className="bg-white border border-slate-200 rounded-sm p-4 flex flex-wrap items-end gap-3">
          <Filter className="w-4 h-4 text-slate-400 self-center" />
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider">Desde</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 rounded-sm font-mono w-40" data-testid="audit-from" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider">Hasta</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 rounded-sm font-mono w-40" data-testid="audit-to" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider">Acción</Label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger className="mt-1 rounded-sm w-40" data-testid="audit-action"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider">Entidad</Label>
            <Select value={entity} onValueChange={setEntity}>
              <SelectTrigger className="mt-1 rounded-sm w-48" data-testid="audit-entity"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {ENTITIES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={load} disabled={loading} className="bg-blue-600 hover:bg-blue-700 rounded-sm h-10" data-testid="audit-apply">
            {loading ? "Cargando…" : "Aplicar"}
          </Button>
          <span className="ml-auto text-xs font-mono text-slate-500 self-center">
            {logs.length} eventos
          </span>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <Th>Fecha/Hora</Th><Th>Usuario</Th><Th>Acción</Th><Th>Entidad</Th><Th>ID</Th><Th>Detalles</Th><Th>IP</Th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr><td colSpan="7" className="p-8 text-center text-slate-500">
                  <Shield className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  Sin eventos para estos filtros
                </td></tr>
              ) : logs.map((l) => (
                <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`audit-row-${l.id}`}>
                  <td className="p-3 font-mono text-xs text-slate-700">{new Date(l.created_at).toLocaleString("es-DO")}</td>
                  <td className="p-3 text-slate-900">
                    <span className="font-mono text-xs">{l.user_email}</span>
                  </td>
                  <td className="p-3">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${ACTION_COLOR[l.action] || "bg-slate-100 text-slate-700"}`}>
                      {l.action}
                    </span>
                  </td>
                  <td className="p-3 font-mono text-xs text-slate-700">{l.entity_type}</td>
                  <td className="p-3 font-mono text-[10px] text-slate-500 max-w-[120px] truncate">{l.entity_id || "—"}</td>
                  <td className="p-3 font-mono text-[10px] text-slate-600 max-w-xs truncate">
                    {Object.keys(l.details || {}).length > 0 ? JSON.stringify(l.details) : "—"}
                  </td>
                  <td className="p-3 font-mono text-[10px] text-slate-500">{l.ip || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Th = ({ children }) => <th className="p-3 text-xs uppercase tracking-wider font-bold text-slate-600">{children}</th>;
